import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  input,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MercerData, PaginatedResult } from '@coin/shared/core/util-models';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { LoadingService } from '@coin/shared/core/data-access';
import { DirectsPositionClassComparisonService } from '../../services/directs-position-comparison.service';
import {
  MercerEvaluationMember,
  MercerEvaluationMemberDetails
} from '../../models/mercer-support-evaluation-member.model';
import { MercerDialogType } from '../../enums/mercer-dialog-type.enum';
import { DirectsPositionClassComparison } from '../../models/directs-position-class-comparison.model';
import { MercerSupportService } from '../../services/mercer-support.service';
import { MercerService } from '../../services/mercer.service';
import { MercerSupportDetailsService } from '../../services/mercer-support-details.service';

@Component({
  selector: 'customer-position-comparison-side-bar',
  templateUrl: './position-comparison-side-bar.component.html',
  styleUrls: ['./position-comparison-side-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PositionComparisonSideBarComponent implements OnInit, OnChanges {
  private mercerSupportService = inject(MercerSupportService);
  private mercerService = inject(MercerService);
  mercerSupportDetailsService = inject(MercerSupportDetailsService);
  private directsPositionClassComparisonService = inject(DirectsPositionClassComparisonService);
  private loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);

  public readonly memberDetails = input<MercerEvaluationMemberDetails>(undefined);
  @Input() public currentMercerData: MercerData;
  public readonly seasonId = input<string>(undefined);
  public readonly dialogType = input<MercerDialogType>(undefined);
  public readonly isLocked = input<boolean>(undefined);
  public readonly hideCareerStreamAndLevel = input<boolean>(undefined);
  public readonly careerStreamValidationErrors = input<string[]>([]);
  public readonly careerStreamValidationWarnings = input<string[]>([]);
  public readonly careerLevelValidationErrors = input<string[]>([]);
  public readonly careerLevelValidationWarnings = input<string[]>([]);

  @Output() public openPosition = new EventEmitter<MercerEvaluationMember>();

  public relatedPositions: Array<MercerEvaluationMember | DirectsPositionClassComparison>;
  public selectedOrgLevel: string;
  public selectedOrgCode: string;

  public MercerDialogType = MercerDialogType;

  public lazyLoadFn =
    (key: string) =>
    (search: string): Observable<string[]> => {
      const dialogType = this.dialogType();
      if (dialogType === MercerDialogType.Support) {
        return this.mercerSupportService
          .getFieldValues(0, 50, key, search, this.seasonId())
          .pipe(map(response => response.content));
      }

      if (dialogType === MercerDialogType.Manager) {
        return this.mercerService
          .getFieldValues(0, 50, key, search, this.seasonId())
          .pipe(map(response => response.content));
      }
      return undefined;
    };

  ngOnInit(): void {
    if (this.dialogType() === MercerDialogType.Headcount) {
      this.getDirectsPositionClassComparison().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.memberDetails && changes.memberDetails?.previousValue?.id !== changes.memberDetails?.currentValue?.id) ||
      changes.memberDetails?.previousValue?.parentOrgCode !== changes.memberDetails?.currentValue?.parentOrgCode ||
      changes.memberDetails?.previousValue?.orgLevel !== changes.memberDetails?.currentValue?.orgLevel
    ) {
      this.setDefaultDropdownValues();
      this.updateRelatedPositions(changes.memberDetails.isFirstChange());
    }
  }

  private setDefaultDropdownValues(): void {
    const memberDetails = this.memberDetails();
    this.selectedOrgCode = memberDetails?.parentOrgCode;
    this.selectedOrgLevel = memberDetails?.orgLevel?.toString();
  }

  public updateRelatedPositions(suppressLoadingScreen = false): void {
    const filterQueryOrgLevel = this.selectedOrgLevel ? `&Query.OrgLevel=${this.selectedOrgLevel}` : '';
    const filterQueryOrgCode = this.selectedOrgCode
      ? `&Query.OrgCodeStartsWith=${encodeURIComponent(this.selectedOrgCode)}`
      : '';
    const filterQuery = filterQueryOrgLevel + filterQueryOrgCode;
    let getMembers$: Observable<PaginatedResult<MercerEvaluationMember>>;

    switch (this.dialogType()) {
      case MercerDialogType.Support:
        getMembers$ = this.mercerSupportService.getEvaluationMembers(this.seasonId(), 0, 15, filterQuery);
        break;
      case MercerDialogType.Manager:
        getMembers$ = this.mercerService.getEvaluationMembers(this.seasonId(), 0, 15, filterQuery);
        break;
      case MercerDialogType.Headcount:
        return;
    }

    getMembers$
      .pipe(
        this.loadingService.suppressLoadingScreen(suppressLoadingScreen),
        tap(result => {
          this.relatedPositions = result?.content?.filter(item => item.id !== this.memberDetails()?.id);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private getDirectsPositionClassComparison(): Observable<DirectsPositionClassComparison[]> {
    const memberDetails = this.memberDetails();
    const positions$ = this.directsPositionClassComparisonService.getDirectsEvaluationMembers(
      memberDetails?.incumbentId,
      memberDetails?.orgCode
    );

    return positions$.pipe(
      tap(result => {
        this.relatedPositions = result?.filter(item => item.id !== this.memberDetails()?.id);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }
}
